(() => {
  const ELEMENT_NAME = 'cuero-orbital-gallery';
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const HOVER_PREFETCH_DELAY = 120;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const lerp = (from, to, amount) => from + (to - from) * amount;
  const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);
  const easeInOutQuint = (value) => value < 0.5
    ? 16 * value * value * value * value * value
    : 1 - Math.pow(-2 * value + 2, 5) / 2;

  class OrbitalShape {
    constructor(item, column, row, density) {
      this.item = item;
      this.column = column;
      this.row = row;
      this.density = density;
      this.theta = density <= 1 ? 0 : (column / (density - 1)) * Math.PI * 2;
      this.phi = density <= 1 ? 0 : (row / (density - 1)) * Math.PI;
      this.x = 0;
      this.y = 0;
      this.z = 0;
      this.size = 0;
      this.alpha = 0;
      this.visible = false;
      this.hovered = false;
    }

    update(rotationX, rotationY, geometry, width, height, baseSize) {
      const sourceX = Math.sin(this.phi) * Math.cos(this.theta);
      const sourceY = Math.cos(this.phi);
      const sourceZ = Math.sin(this.phi) * Math.sin(this.theta);

      const cosY = Math.cos(rotationY);
      const sinY = Math.sin(rotationY);
      const cosX = Math.cos(rotationX);
      const sinX = Math.sin(rotationX);

      const x1 = sourceX * cosY + sourceZ * sinY;
      const z1 = -sourceX * sinY + sourceZ * cosY;
      const y1 = sourceY;

      const y2 = y1 * cosX - z1 * sinX;
      const z2 = y1 * sinX + z1 * cosX;

      const depth = (z2 + 1) / 2;

      this.x = geometry.centerX + x1 * geometry.radiusX;
      this.y = geometry.centerY + y2 * geometry.radiusY;
      this.z = z2;
      this.alpha = clamp((depth - 0.12) / 0.78, 0, 1);
      this.size = baseSize * (0.56 + depth * 1.05);
      this.visible = depth > 0.1 && this.x > -this.size && this.x < width + this.size && this.y > -this.size && this.y < height + this.size;
    }

    contains(pointer) {
      if (!this.visible || !pointer.active) return false;
      return Math.abs(pointer.x - this.x) <= this.size / 2 && Math.abs(pointer.y - this.y) <= this.size / 2;
    }

    draw(ctx, focusColor, dimAmount) {
      if (!this.visible || !this.item.image) return;

      const image = this.item.image;
      const size = this.size;
      const radius = Math.max(1, size * 0.04);
      const alpha = this.alpha * dimAmount;

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(this.x, this.y);

      roundedRect(ctx, -size / 2, -size / 2, size, size, radius);
      ctx.clip();
      drawImageCover(ctx, image, -size / 2, -size / 2, size, size);

      ctx.globalAlpha = alpha * 0.36;
      ctx.fillStyle = 'rgba(255,255,255,0.08)';
      ctx.fillRect(-size / 2, -size / 2, size, size * 0.42);
      ctx.restore();

      if (this.hovered) {
        ctx.save();
        ctx.globalAlpha = 0.94;
        ctx.strokeStyle = focusColor;
        ctx.lineWidth = 1.5;
        roundedRect(ctx, this.x - size / 2 - 4, this.y - size / 2 - 4, size + 8, size + 8, radius + 3);
        ctx.stroke();
        ctx.restore();
      }
    }
  }

  class MainImage {
    constructor(gallery) {
      this.gallery = gallery;
      this.item = null;
      this.active = false;
      this.closing = false;
      this.startedAt = 0;
      this.progress = 0;
      this.strips = [];
      this.lastStripKey = '';
    }

    open(item) {
      if (!item || !item.image) return;
      this.item = item;
      this.active = true;
      this.closing = false;
      this.startedAt = performance.now();
      this.progress = 0;
      this.strips = [];
      this.lastStripKey = '';
      this.gallery.hideViewer();
      this.gallery.loadHdImage(item);
      this.gallery.classList.add('is-main-open');
    }

    close() {
      if (!this.active || this.closing) return;
      this.closing = true;
      this.startedAt = performance.now();
      // Hand the photo back to the canvas so it can play the closing animation.
      this.gallery.hideViewer();
    }

    getImage() {
      if (!this.item) return null;
      return this.item.hdImage || this.item.image;
    }

    draw(ctx, width, height, now) {
      if (!this.active || !this.item || !this.item.image) return;

      const duration = this.gallery.reducedMotion ? 1 : 820;
      const closeDuration = this.gallery.reducedMotion ? 1 : 360;
      const elapsed = now - this.startedAt;
      const rawProgress = this.closing
        ? clamp(1 - elapsed / closeDuration, 0, 1)
        : clamp(elapsed / duration, 0, 1);
      const visibleProgress = this.closing ? easeInOutQuint(rawProgress) : easeOutCubic(rawProgress);
      const settled = !this.closing && rawProgress >= 1;
      const box = this.gallery.getViewerBox(this.item);

      this.progress = visibleProgress;
      this.drawOverlay(ctx, width, height, visibleProgress);

      // Once the opening animation has finished, the photo is shown as a real <img>
      // on top of the canvas: the browser displays the file itself at the screen's
      // full resolution, in its exact proportions and colours.
      const viewerOnTop = settled && this.gallery.showViewer(this.item, box);
      this.drawImageStrips(ctx, box, visibleProgress, now, settled, viewerOnTop);

      if (settled) {
        this.progress = 1;
      }

      if (this.closing && rawProgress <= 0) {
        this.active = false;
        this.item = null;
        this.strips = [];
        this.lastStripKey = '';
        this.gallery.classList.remove('is-main-open');
      }
    }

    drawOverlay(ctx, width, height, progress) {
      ctx.save();
      ctx.globalAlpha = progress * this.gallery.overlayOpacity;
      ctx.fillStyle = '#050403';
      ctx.fillRect(0, 0, width, height);
      ctx.restore();
    }

    drawImageStrips(ctx, box, progress, now, settled, viewerOnTop) {
      const image = this.getImage();
      const dpr = this.gallery.dpr || 1;
      const naturalWidth = image.naturalWidth || 1;
      const naturalHeight = image.naturalHeight || 1;
      const stripKey = String(box.height);

      if (this.lastStripKey !== stripKey) {
        this.lastStripKey = stripKey;
        this.strips = createStrips(box.height);
      }

      ctx.save();
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.globalAlpha = progress;

      // Cast one drop shadow for the whole image: the image itself is drawn far
      // off-canvas and only its shadow is offset back into place. Shadowing each
      // strip separately produced dark horizontal bands across the image.
      // Shadow offsets/blur are in device pixels, so they are scaled by dpr.
      const shadowShift = this.gallery.width + box.width + 200;
      ctx.shadowColor = 'rgba(0, 0, 0, 0.42)';
      ctx.shadowBlur = 28 * progress * dpr;
      ctx.shadowOffsetX = shadowShift * dpr;
      ctx.shadowOffsetY = 22 * progress * dpr;
      ctx.drawImage(image, box.x - shadowShift, box.y, box.width, box.height);

      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      if (settled) {
        // Fully open: one undistorted draw, until the <img> on top has taken over.
        if (!viewerOnTop) ctx.drawImage(image, box.x, box.y, box.width, box.height);
      } else {
        const naturalScale = naturalHeight / box.height;

        this.strips.forEach((strip, index) => {
          const delay = index / Math.max(this.strips.length - 1, 1);
          const stripProgress = clamp((progress - delay * 0.18) / 0.82, 0, 1);
          const eased = easeOutCubic(stripProgress);
          const wave = Math.sin(index * 0.72 + now * 0.012) * 10;
          const direction = index % 2 === 0 ? 1 : -1;
          const openOffset = strip.offset * (1 - eased);
          const closeOffset = this.closing ? direction * (1 - progress) * (50 + Math.abs(strip.offset) * 0.5 + wave) : 0;

          // Strip edges are snapped to device pixels so neighbouring strips meet
          // exactly (no gaps, no double-drawn rows), and each strip samples a source
          // slice with the same height ratio, so the photo is never stretched.
          const top = snapToPixel(box.y + strip.y, dpr);
          const bottom = snapToPixel(box.y + strip.y + strip.height, dpr);
          const sourceY = (top - box.y) * naturalScale;
          const sourceHeight = Math.min((bottom - top) * naturalScale, naturalHeight - sourceY);
          if (sourceHeight <= 0) return;

          ctx.drawImage(
            image,
            0,
            sourceY,
            naturalWidth,
            sourceHeight,
            box.x + openOffset + closeOffset,
            top,
            box.width,
            sourceHeight / naturalScale
          );
        });
      }

      ctx.globalAlpha = progress * 0.78;
      ctx.strokeStyle = this.gallery.focusColor;
      ctx.lineWidth = 1;
      ctx.strokeRect(box.x - 8, box.y - 8, box.width + 16, box.height + 16);
      ctx.restore();
    }
  }

  class CueroOrbitalGallery extends HTMLElement {
    constructor() {
      super();

      this.canvas = null;
      this.ctx = null;
      this.stage = null;
      this.loader = null;
      this.counter = null;
      this.line = null;
      this.images = [];
      this.shapes = [];
      this.hoveredShape = null;
      this.mainImage = null;
      this.viewer = null;
      this.viewerBoxKey = '';
      this.viewerFrames = 0;
      this.hoverItem = null;
      this.hoverSince = 0;
      this.raf = 0;
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.onScreen = true;
      this.width = 0;
      this.height = 0;
      this.dpr = 1;
      this.rotation = { x: -0.1, y: 0 };
      this.targetRotation = { x: -0.1, y: 0 };
      this.pointerTilt = { x: 0, y: 0 };
      this.pointer = { x: 0, y: 0, active: false };
      this.touch = { x: 0, y: 0, active: false };
      this.lastFrame = 0;
      this.ready = false;
      this.connectionToken = 0;

      this.onResize = this.onResize.bind(this);
      this.onWheel = this.onWheel.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onVisibilityChange = this.updateLoop.bind(this);
      this.render = this.render.bind(this);
    }

    connectedCallback() {
      if (this.ready) return;

      this.connectionToken += 1;
      const connectionToken = this.connectionToken;

      this.stage = this.querySelector('.cog__stage');
      this.canvas = this.querySelector('[data-cog-canvas]');
      this.loader = this.querySelector('[data-cog-loader]');
      this.counter = this.querySelector('[data-cog-counter]');
      this.line = this.querySelector('[data-cog-line]');
      this.ctx = this.canvas ? this.canvas.getContext('2d') : null;
      this.reducedMotion = prefersReducedMotion.matches;
      this.saveData = Boolean(navigator.connection && navigator.connection.saveData);
      this.density = clamp(parseInt(this.dataset.density || '16', 10), 8, 18);
      this.desktopOrbitScale = clamp(parseFloat(this.dataset.desktopOrbitScale || '100') / 100, 0.4, 1.2);
      this.mobileOrbitScale = clamp(parseFloat(this.dataset.mobileOrbitScale || '100') / 100, 0.4, 1.2);
      this.desktopOrbitX = clamp(parseFloat(this.dataset.desktopOrbitX || '52') / 100, 0, 1);
      this.desktopOrbitY = clamp(parseFloat(this.dataset.desktopOrbitY || '56') / 100, 0, 1);
      this.mobileOrbitX = clamp(parseFloat(this.dataset.mobileOrbitX || '52') / 100, 0, 1);
      this.mobileOrbitY = clamp(parseFloat(this.dataset.mobileOrbitY || '61') / 100, 0, 1);
      this.tileScale = clamp(parseFloat(this.dataset.tileScale || '80') / 100, 0.25, 1.8);
      this.sensitivity = clamp(parseFloat(this.dataset.sensitivity || '100') / 100, 0.35, 4);
      this.motionDirection = this.dataset.motionDirection === 'reverse' ? -1 : 1;
      this.openOnClick = this.dataset.openOnClick !== 'false';
      this.captureScroll = this.dataset.captureScroll !== 'false';
      this.overlayOpacity = clamp(parseFloat(getComputedStyle(this).getPropertyValue('--cog-overlay-opacity')) || 0.76, 0.3, 1);
      this.focusColor = getComputedStyle(this).getPropertyValue('--cog-focus').trim() || '#c7a66d';
      this.mainImage = new MainImage(this);

      if (!this.stage || !this.canvas || !this.ctx) return;

      this.setAttribute('aria-busy', 'true');
      this.installEvents();
      this.onResize();
      this.preloadImages(connectionToken);
    }

    disconnectedCallback() {
      this.connectionToken += 1;
      this.removeEvents();
      this.hideViewer();
      cancelAnimationFrame(this.raf);
      this.raf = 0;
      this.ready = false;
    }

    installEvents() {
      window.addEventListener('resize', this.onResize);
      this.stage.addEventListener('wheel', this.onWheel, { passive: false });
      this.stage.addEventListener('pointermove', this.onPointerMove);
      this.stage.addEventListener('pointerleave', this.onPointerLeave);
      this.stage.addEventListener('click', this.onClick);
      this.stage.addEventListener('touchstart', this.onTouchStart, { passive: false });
      this.stage.addEventListener('touchmove', this.onTouchMove, { passive: !this.captureScroll });
      this.stage.addEventListener('touchend', this.onTouchEnd);
      this.stage.addEventListener('touchcancel', this.onTouchEnd);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.stage);
      }

      // Only draw while the gallery is (nearly) on screen and the tab is visible. Drawing
      // hundreds of tiles every frame off-screen slowed every other section on phones.
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          this.onScreen = entries[entries.length - 1].isIntersecting;
          this.updateLoop();
        }, { rootMargin: '200px 0px' });
        this.intersectionObserver.observe(this.stage);
      }
    }

    removeEvents() {
      window.removeEventListener('resize', this.onResize);

      if (this.stage) {
        this.stage.removeEventListener('wheel', this.onWheel);
        this.stage.removeEventListener('pointermove', this.onPointerMove);
        this.stage.removeEventListener('pointerleave', this.onPointerLeave);
        this.stage.removeEventListener('click', this.onClick);
        this.stage.removeEventListener('touchstart', this.onTouchStart);
        this.stage.removeEventListener('touchmove', this.onTouchMove);
        this.stage.removeEventListener('touchend', this.onTouchEnd);
        this.stage.removeEventListener('touchcancel', this.onTouchEnd);
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
    }

    shouldRender() {
      return this.ready && this.isConnected && this.onScreen && !document.hidden;
    }

    // Starts or stops the drawing loop to match shouldRender().
    updateLoop() {
      if (this.shouldRender()) {
        if (!this.raf) {
          this.lastFrame = performance.now();
          this.raf = requestAnimationFrame(this.render);
        }
      } else if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = 0;
      }
    }

    readImageData() {
      const jsonNode = this.querySelector('[data-cog-images]');
      if (!jsonNode) return [];

      try {
        const parsed = JSON.parse(jsonNode.textContent || '[]');
        if (!Array.isArray(parsed)) return [];

        return parsed
          .filter((item) => item && (item.thumb || item.src))
          .map((item, index) => {
            const width = Number(item.width) || 0;
            const height = Number(item.height) || 0;
            const sources = (Array.isArray(item.sources) ? item.sources : [])
              .filter((source) => source && source.url && Number(source.w) > 0)
              .map((source) => ({ url: source.url, w: Number(source.w) }))
              .sort((a, b) => a.w - b.w);

            if (!sources.length && (item.full || item.src)) {
              sources.push({ url: item.full || item.src, w: width || 2000 });
            }

            return {
              thumb: item.thumb || item.src,
              sources,
              ratio: width > 0 && height > 0 ? width / height : 0,
              alt: item.alt || item.title || `Cuero gallery image ${index + 1}`,
              title: item.title || ''
            };
          });
      } catch (error) {
        return [];
      }
    }

    preloadImages(connectionToken = this.connectionToken) {
      const items = this.readImageData();

      if (!items.length) {
        this.classList.add('is-empty');
        this.markLoaded();
        return;
      }

      let settled = 0;
      const update = () => {
        settled += 1;
        this.updateLoader(Math.round((settled / items.length) * 100));
      };

      // The orbit tiles are small, so they use the light thumbnail. The HD photo is
      // fetched when a frame is hovered, touched or opened (see loadHdImage).
      const promises = items.map((item) => new Promise((resolve) => {
        const image = new Image();
        image.decoding = 'async';
        image.onload = () => {
          item.image = image;
          update();
          resolve(item);
        };
        image.onerror = () => {
          update();
          resolve(null);
        };
        image.src = item.thumb;
      }));

      Promise.all(promises).then((loadedItems) => {
        if (!this.isConnected || connectionToken !== this.connectionToken) return;

        this.images = loadedItems.filter(Boolean);

        if (!this.images.length) {
          this.classList.add('is-empty');
          this.markLoaded();
          return;
        }

        this.buildShapes();
        this.ready = true;
        this.classList.add('is-ready');
        this.markLoaded();
        this.updateLoop();
      });
    }

    getItemRatio(item) {
      if (item.ratio) return item.ratio;
      const image = item.hdImage || item.image;
      return image && image.naturalWidth && image.naturalHeight ? image.naturalWidth / image.naturalHeight : 1;
    }

    getViewerBox(item) {
      return fitBox(this.getItemRatio(item), this.width, this.height, this.dpr);
    }

    // Loads the smallest size that is still at least as wide as the opened photo is
    // in real screen pixels (up to the original upload), so it is always shown sharp
    // without downloading more than the screen can display.
    loadHdImage(item) {
      if (!item || !item.sources || !item.sources.length || this.width <= 1) return;

      const screenDensity = clamp(window.devicePixelRatio || 1, 1, 4);
      const neededWidth = this.getViewerBox(item).width * screenDensity;
      const source = item.sources.find((entry) => entry.w >= neededWidth) || item.sources[item.sources.length - 1];

      // Never downgrade or double-load: keep an image that is already as sharp.
      if ((item.hdWidth || 0) >= source.w || (item.hdLoadingWidth || 0) >= source.w) return;

      item.hdLoadingWidth = source.w;
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => {
        const done = () => {
          if (item.hdLoadingWidth === source.w) item.hdLoadingWidth = 0;
          if ((item.hdWidth || 0) >= source.w) return;
          item.hdImage = image;
          item.hdWidth = source.w;
        };
        if (typeof image.decode === 'function') {
          image.decode().then(done, done);
        } else {
          done();
        }
      };
      image.onerror = () => {
        if (item.hdLoadingWidth === source.w) item.hdLoadingWidth = 0;
        item.sources = item.sources.filter((entry) => entry !== source);
      };
      image.src = source.url;
    }

    // Warms up the HD photo of a frame the pointer rests on, before it is clicked.
    prefetchOnHover(item, now) {
      if (this.saveData) return;

      if (this.hoverItem !== item) {
        this.hoverItem = item;
        this.hoverSince = now;
      } else if (now - this.hoverSince >= HOVER_PREFETCH_DELAY) {
        this.loadHdImage(item);
      }
    }

    // Shows the opened photo as a real <img> element exactly over the canvas copy.
    // Returns true once the canvas copy underneath can stop being drawn.
    showViewer(item, box) {
      const image = item.hdImage;
      if (!image || !this.stage) return false;

      if (this.viewer !== image) {
        this.hideViewer();
        image.className = 'cog__viewer';
        image.alt = item.alt;
        image.draggable = false;
        this.stage.appendChild(image);
        this.viewer = image;
      }

      const boxKey = `${box.x}:${box.y}:${box.width}:${box.height}`;
      if (this.viewerBoxKey !== boxKey) {
        this.viewerBoxKey = boxKey;
        image.style.setProperty('left', `${box.x}px`, 'important');
        image.style.setProperty('top', `${box.y}px`, 'important');
        image.style.setProperty('width', `${box.width}px`, 'important');
        image.style.setProperty('height', `${box.height}px`, 'important');
      }

      // For its first frame the canvas copy stays underneath, so the photo never
      // blinks while the <img> is being painted.
      this.viewerFrames += 1;
      return this.viewerFrames > 1;
    }

    hideViewer() {
      if (this.viewer && this.viewer.parentNode) {
        this.viewer.parentNode.removeChild(this.viewer);
      }
      this.viewer = null;
      this.viewerBoxKey = '';
      this.viewerFrames = 0;
    }

    updateLoader(percent) {
      const progress = clamp(percent, 0, 100);

      if (this.counter) this.counter.textContent = `${progress}%`;
      if (this.line) this.line.style.setProperty('--cog-progress', `${progress}%`);
    }

    markLoaded() {
      this.updateLoader(100);
      this.setAttribute('aria-busy', 'false');

      if (this.loader) {
        window.setTimeout(() => {
          this.loader.classList.add('is-loaded');
        }, this.reducedMotion ? 0 : 180);
      }
    }

    buildShapes() {
      this.shapes = [];
      const total = this.density * this.density;

      for (let index = 0; index < total; index += 1) {
        const column = index % this.density;
        const row = Math.floor(index / this.density);
        const item = this.images[index % this.images.length];
        this.shapes.push(new OrbitalShape(item, column, row, this.density));
      }
    }

    onResize() {
      if (!this.stage || !this.canvas || !this.ctx) return;

      const rect = this.stage.getBoundingClientRect();
      this.width = Math.max(1, Math.round(rect.width));
      this.height = Math.max(1, Math.round(rect.height));
      this.dpr = clamp(window.devicePixelRatio || 1, 1, 2);
      this.canvas.width = Math.round(this.width * this.dpr);
      this.canvas.height = Math.round(this.height * this.dpr);
      this.canvas.style.width = `${this.width}px`;
      this.canvas.style.height = `${this.height}px`;
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

      // A bigger window or a zoomed page may need a sharper version of the open photo.
      if (this.mainImage && this.mainImage.active && !this.mainImage.closing) {
        this.loadHdImage(this.mainImage.item);
      }
    }

    getBaseSize() {
      return Math.max(16, Math.min(this.width, this.height) / (this.density * 0.74) * this.tileScale);
    }

    getOrbitGeometry() {
      const isDesktop = this.width > 749;
      const scale = isDesktop ? this.desktopOrbitScale : this.mobileOrbitScale;
      const centerX = isDesktop ? this.desktopOrbitX : this.mobileOrbitX;
      const centerY = isDesktop ? this.desktopOrbitY : this.mobileOrbitY;
      const radiusY = Math.min(this.width, this.height) * 0.48 * scale;

      return {
        centerX: this.width * centerX,
        centerY: this.height * centerY,
        radiusX: radiusY * (isDesktop ? 1.55 : 1.02),
        radiusY
      };
    }

    isPointInsideOrbit(clientX, clientY) {
      if (!this.stage) return false;

      const rect = this.stage.getBoundingClientRect();
      const geometry = this.getOrbitGeometry();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const normalizedX = (x - geometry.centerX) / Math.max(geometry.radiusX, 1);
      const normalizedY = (y - geometry.centerY) / Math.max(geometry.radiusY, 1);

      return normalizedX * normalizedX + normalizedY * normalizedY <= 1;
    }

    shapeAtClientPoint(clientX, clientY) {
      const rect = this.stage.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;

      return this.shapes
        .filter((shape) => shape.visible)
        .sort((a, b) => b.z - a.z)
        .find((shape) => (
          Math.abs(x - shape.x) <= shape.size / 2
          && Math.abs(y - shape.y) <= shape.size / 2
        )) || null;
    }

    onWheel(event) {
      if (!this.ready || !this.isPointInsideOrbit(event.clientX, event.clientY)) return;
      if (this.captureScroll && event.cancelable) event.preventDefault();

      const deltaX = clamp(event.deltaX, -120, 120);
      const deltaY = clamp(event.deltaY, -120, 120);
      this.targetRotation.y += (deltaX * 0.0012 + deltaY * 0.0009) * this.sensitivity * this.motionDirection;
      this.targetRotation.x += deltaY * 0.00055 * this.sensitivity * this.motionDirection;
      this.targetRotation.x = clamp(this.targetRotation.x, -Math.PI * 0.46, Math.PI * 0.46);
    }

    onPointerMove(event) {
      if (!this.stage) return;

      if (!this.isPointInsideOrbit(event.clientX, event.clientY)) {
        this.onPointerLeave();
        return;
      }

      this.classList.add('is-in-orbit');

      const rect = this.stage.getBoundingClientRect();
      const geometry = this.getOrbitGeometry();
      this.pointer.x = event.clientX - rect.left;
      this.pointer.y = event.clientY - rect.top;
      this.pointer.active = true;
      this.pointerTilt.x = ((this.pointer.y - geometry.centerY) / Math.max(geometry.radiusY, 1)) * 0.16 * this.sensitivity * this.motionDirection;
      this.pointerTilt.y = ((this.pointer.x - geometry.centerX) / Math.max(geometry.radiusX, 1)) * -0.21 * this.sensitivity * this.motionDirection;
    }

    onPointerLeave() {
      this.pointer.active = false;
      this.pointerTilt.x = 0;
      this.pointerTilt.y = 0;
      this.classList.remove('is-in-orbit');
      this.classList.remove('has-hover');
    }

    onClick(event) {
      if (!this.openOnClick) return;

      if (this.mainImage && this.mainImage.active) {
        this.mainImage.close();
        return;
      }

      if (!event || !this.isPointInsideOrbit(event.clientX, event.clientY)) return;

      const clickedShape = this.shapeAtClientPoint(event.clientX, event.clientY);
      if (clickedShape) this.mainImage.open(clickedShape.item);
    }

    onTouchStart(event) {
      if (!event.touches || !event.touches.length) return;

      const touch = event.touches[0];
      this.touch.active = this.isPointInsideOrbit(touch.clientX, touch.clientY);
      this.touch.x = touch.clientX;
      this.touch.y = touch.clientY;

      // Phones have no hover, so start fetching the HD photo as soon as a frame is touched.
      if (this.touch.active && this.ready && !this.saveData && !(this.mainImage && this.mainImage.active)) {
        const shape = this.shapeAtClientPoint(touch.clientX, touch.clientY);
        if (shape) this.loadHdImage(shape.item);
      }
    }

    onTouchMove(event) {
      if (!this.ready || !this.touch.active || !event.touches || !event.touches.length) return;

      const touch = event.touches[0];
      if (!this.isPointInsideOrbit(touch.clientX, touch.clientY)) {
        this.touch.active = false;
        return;
      }

      if (this.captureScroll && event.cancelable) event.preventDefault();

      const moveX = this.touch.x - touch.clientX;
      const moveY = this.touch.y - touch.clientY;
      this.touch.x = touch.clientX;
      this.touch.y = touch.clientY;

      this.targetRotation.y += moveX * 0.0045 * this.sensitivity * this.motionDirection;
      this.targetRotation.x += moveY * 0.0034 * this.sensitivity * this.motionDirection;
      this.targetRotation.x = clamp(this.targetRotation.x, -Math.PI * 0.46, Math.PI * 0.46);
    }

    onTouchEnd() {
      this.touch.active = false;
    }

    render(now) {
      const dt = clamp(now - this.lastFrame, 0, 50);
      this.lastFrame = now;

      this.ctx.clearRect(0, 0, this.width, this.height);

      if (!this.reducedMotion && !(this.mainImage && this.mainImage.active)) {
        this.targetRotation.y += dt * 0.00008 * this.sensitivity;
      }

      this.rotation.x = lerp(this.rotation.x, this.targetRotation.x + this.pointerTilt.x, this.reducedMotion ? 0.28 : 0.08);
      this.rotation.y = lerp(this.rotation.y, this.targetRotation.y + this.pointerTilt.y, this.reducedMotion ? 0.28 : 0.08);

      const baseSize = this.getBaseSize();
      const geometry = this.getOrbitGeometry();
      let hoveredShape = null;

      this.shapes.forEach((shape) => {
        shape.hovered = false;
        shape.update(this.rotation.x, this.rotation.y, geometry, this.width, this.height, baseSize);
      });

      const visibleShapes = this.shapes
        .filter((shape) => shape.visible)
        .sort((a, b) => a.z - b.z);

      for (let index = visibleShapes.length - 1; index >= 0; index -= 1) {
        const shape = visibleShapes[index];
        if (!hoveredShape && shape.contains(this.pointer)) {
          hoveredShape = shape;
        }
      }

      this.hoveredShape = hoveredShape;
      this.classList.toggle('has-hover', Boolean(hoveredShape) && !(this.mainImage && this.mainImage.active));

      if (hoveredShape && !(this.mainImage && this.mainImage.active)) {
        hoveredShape.hovered = true;
        this.prefetchOnHover(hoveredShape.item, now);
      } else {
        this.hoverItem = null;
      }

      const dimAmount = this.mainImage && this.mainImage.active ? 0.34 : 1;
      visibleShapes.forEach((shape) => {
        shape.draw(this.ctx, this.focusColor, dimAmount);
      });

      this.drawGlints(now);

      if (this.mainImage) {
        this.mainImage.draw(this.ctx, this.width, this.height, now);
      }

      this.raf = this.shouldRender() ? requestAnimationFrame(this.render) : 0;
    }

    drawGlints(now) {
      if (this.reducedMotion || (this.mainImage && this.mainImage.active)) return;
      const pulse = (Math.sin(now * 0.0012) + 1) / 2;
      const geometry = this.getOrbitGeometry();

      this.ctx.save();
      this.ctx.globalAlpha = 0.08 + pulse * 0.06;
      this.ctx.strokeStyle = this.focusColor;
      this.ctx.lineWidth = 1;
      this.ctx.beginPath();
      this.ctx.ellipse(
        geometry.centerX,
        geometry.centerY,
        geometry.radiusX * (this.width > 749 ? 0.9 : 0.74),
        geometry.radiusY * (this.width > 749 ? 0.31 : 0.58),
        -0.1,
        0,
        Math.PI * 2
      );
      this.ctx.stroke();
      this.ctx.restore();
    }
  }

  function createStrips(height) {
    const strips = [];
    let y = 0;
    let index = 0;

    while (y < height) {
      const stripHeight = Math.min(height - y, 7 + ((index * 11) % 18));
      const direction = index % 2 === 0 ? 1 : -1;
      strips.push({
        y,
        height: stripHeight,
        offset: direction * (72 + ((index * 37) % 110))
      });
      y += stripHeight;
      index += 1;
    }

    return strips;
  }

  function snapToPixel(value, dpr) {
    return Math.round(value * dpr) / dpr;
  }

  // Where the opened photo sits. The height is snapped down to whole device pixels
  // (so the reveal strips meet exactly) and the width is derived from it, so the box
  // always has the photo's exact original proportions.
  function fitBox(ratio, width, height, dpr) {
    const isDesktop = width > 749;
    const maxWidth = isDesktop ? width * 0.68 : width * 0.84;
    const maxHeight = isDesktop ? height * 0.74 : height * 0.58;
    const boxHeight = Math.max(1, Math.floor(Math.min(maxHeight, maxWidth / ratio) * dpr) / dpr);
    const boxWidth = boxHeight * ratio;

    return {
      x: snapToPixel((width - boxWidth) / 2, dpr),
      y: snapToPixel(isDesktop ? (height - boxHeight) / 2 + height * 0.04 : height * 0.34, dpr),
      width: boxWidth,
      height: boxHeight
    };
  }

  function drawImageCover(ctx, image, x, y, width, height) {
    const naturalWidth = image.naturalWidth || width;
    const naturalHeight = image.naturalHeight || height;
    const imageRatio = naturalWidth / naturalHeight;
    const boxRatio = width / height;
    let sourceWidth = naturalWidth;
    let sourceHeight = naturalHeight;
    let sourceX = 0;
    let sourceY = 0;

    if (imageRatio > boxRatio) {
      sourceWidth = naturalHeight * boxRatio;
      sourceX = (naturalWidth - sourceWidth) / 2;
    } else {
      sourceHeight = naturalWidth / boxRatio;
      sourceY = (naturalHeight - sourceHeight) / 2;
    }

    ctx.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, x, y, width, height);
  }

  function roundedRect(ctx, x, y, width, height, radius) {
    const r = Math.min(radius, width / 2, height / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + width, y, x + width, y + height, r);
    ctx.arcTo(x + width, y + height, x, y + height, r);
    ctx.arcTo(x, y + height, x, y, r);
    ctx.arcTo(x, y, x + width, y, r);
    ctx.closePath();
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, CueroOrbitalGallery);
  }
})();
